import { useCallback, useEffect, useMemo, useState } from "react";
import { IKnowledgeArticleModel, KnowledgeArticleService } from "../../../Services/KnowledgeArticleService";
import { IMediaAssetModel, MediaAssetService } from "../../../Services/MediaAssetService";
import { useMediaUrlRefresh } from "../../../Pages/KnowledgeBase/useMediaUrlRefresh";

export interface KnowledgeArticleEmbedState {
    article: IKnowledgeArticleModel | null;
    mediaAssets: IMediaAssetModel[];
    loading: boolean;
    /** The article could not be loaded: it is gone, or not (or no longer) available to this user. */
    failed: boolean;
}

/** An article for showing inside another page: the article (sections included) and its media (signed URLs
 *  included) — two requests next to each other, whatever the number of images. */
export const useKnowledgeArticleEmbed = (articleUuid: string | null): KnowledgeArticleEmbedState => {
    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const mediaAssetService = useMemo(() => new MediaAssetService(), []);
    const [article, setArticle] = useState<IKnowledgeArticleModel | null>(null);
    const [mediaAssets, setMediaAssets] = useState<IMediaAssetModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setArticle(null);
        setMediaAssets([]);
        setFailed(false);
        if (!articleUuid) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        knowledgeArticleService.registerView(articleUuid).catch(() => undefined);
        Promise.all([
            knowledgeArticleService.fetchItem(articleUuid),
            // Without its media an article is still worth showing.
            mediaAssetService.fetchByArticle(articleUuid).catch(() => null),
        ])
            .then(([articleResponse, mediaResponse]) => {
                if (!cancelled) {
                    setArticle(articleResponse.data.data ?? null);
                    setMediaAssets(mediaResponse?.data.data ?? []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFailed(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [articleUuid, knowledgeArticleService, mediaAssetService]);

    const reloadMedia = useCallback(() => {
        if (!articleUuid) {
            return;
        }
        mediaAssetService
            .fetchByArticle(articleUuid)
            .then((response) => setMediaAssets(response.data.data ?? []))
            .catch(() => undefined);
    }, [articleUuid, mediaAssetService]);

    useMediaUrlRefresh(reloadMedia);

    return { article, mediaAssets, loading, failed };
};
